import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import ContractorCostQuotesReviewApi from "../../../../../infrastructure/api/contractorCostQuotesReview";
import EquipmentPublicCodeChip from "../equipmentCatalog/EquipmentPublicCodeChip";
import CustomerPriceOffersReviewApi from "../../../../../infrastructure/api/customerPriceOffersReview";
import CustomerContractAcceptanceReviewApi from "../../../../../infrastructure/api/customerContractAcceptanceReview";
import ProjectWorkflowFoundationApi from "../../../../../infrastructure/api/projectWorkflowFoundation";
import {
  CONTRACTOR_COST_STATUS_CODES,
  CUSTOMER_OFFER_MISSING_MARGIN_MESSAGE,
  CUSTOMER_OFFER_STATUS_CODES,
  DEFAULT_CUSTOMER_OFFER_MARGIN_PERCENT,
  PAYMENT_SCHEDULE_TITLES,
  buildDefaultPaymentSchedule,
  hasCustomerOfferPlatformMargin,
  normalizeContractorCostQuoteStatus,
  normalizeCustomerPriceOfferStatus,
  normalizePaymentSchedule,
  paymentScheduleFromForm,
  paymentScheduleToForm,
  previewCustomerOfferVat,
  previewCustomerPriceFinal,
} from "../../../../../infrastructure/utils/pricingQuoteStatus";
import { toJalaliFullDate } from "../../../../middleware/middleware";

const COST_STATUS_DRAFT = CONTRACTOR_COST_STATUS_CODES.Draft;
const COST_STATUS_SUBMITTED = CONTRACTOR_COST_STATUS_CODES.Submitted;
const COST_STATUS_APPROVED = CONTRACTOR_COST_STATUS_CODES.Approved;
const COST_STATUS_REJECTED = CONTRACTOR_COST_STATUS_CODES.Rejected;
const OFFER_STATUS_DRAFT = CUSTOMER_OFFER_STATUS_CODES.Draft;
const OFFER_STATUS_APPROVED = CUSTOMER_OFFER_STATUS_CODES.ApprovedBySolarent;
const OFFER_STATUS_ACCEPTED = CUSTOMER_OFFER_STATUS_CODES.AcceptedByCustomer;
const CONTRACT_REVIEW_UPLOADED = 2;
const CONTRACT_REVIEW_APPROVED = 3;

const normalizeProjectContractSummary = (raw) => ({
  projectContractId: raw?.projectContractId ?? raw?.ProjectContractId,
  requestId: raw?.requestId ?? raw?.RequestId,
  contractAmount: raw?.contractAmount ?? raw?.ContractAmount,
  price: raw?.price ?? raw?.Price ?? 0,
  isConditionalOnLoanDisbursement:
    raw?.isConditionalOnLoanDisbursement ?? raw?.IsConditionalOnLoanDisbursement ?? false,
  conditionalReason: raw?.conditionalReason ?? raw?.ConditionalReason ?? "",
  projectContractStatus: raw?.projectContractStatus ?? raw?.ProjectContractStatus,
  projectContractStatusTitle:
    raw?.projectContractStatusTitle ?? raw?.ProjectContractStatusTitle ?? "",
  customerPriceOfferId: raw?.customerPriceOfferId ?? raw?.CustomerPriceOfferId,
  canGenerateFromCustomerOffer:
    raw?.canGenerateFromCustomerOffer ?? raw?.CanGenerateFromCustomerOffer ?? false,
  generateBlockReason: raw?.generateBlockReason ?? raw?.GenerateBlockReason ?? "",
  loanGate: {
    loanGateRequired: raw?.loanGate?.loanGateRequired ?? raw?.LoanGate?.LoanGateRequired ?? false,
    loanApproved: raw?.loanGate?.loanApproved ?? raw?.LoanGate?.LoanApproved ?? false,
    loanDisbursed: raw?.loanGate?.loanDisbursed ?? raw?.LoanGate?.LoanDisbursed ?? false,
    exemptReason: raw?.loanGate?.exemptReason ?? raw?.LoanGate?.ExemptReason ?? "",
    blockingReason: raw?.loanGate?.blockingReason ?? raw?.LoanGate?.BlockingReason ?? "",
    finalApprovalAllowed:
      raw?.loanGate?.finalApprovalAllowed ?? raw?.LoanGate?.FinalApprovalAllowed ?? true,
    finalApprovalBlockReason:
      raw?.loanGate?.finalApprovalBlockReason ?? raw?.LoanGate?.FinalApprovalBlockReason ?? "",
  },
});

const normalizeCustomerOffer = (raw) => ({
  id: raw?.id ?? raw?.Id,
  requestId: raw?.requestId ?? raw?.RequestId,
  contractorCostQuoteId: raw?.contractorCostQuoteId ?? raw?.ContractorCostQuoteId,
  status: normalizeCustomerPriceOfferStatus(raw?.status ?? raw?.Status),
  statusTitle: raw?.statusTitle ?? raw?.StatusTitle ?? "",
  totalContractorCost: raw?.totalContractorCost ?? raw?.TotalContractorCost ?? 0,
  customerPriceSubtotal: raw?.customerPriceSubtotal ?? raw?.CustomerPriceSubtotal ?? 0,
  customerPriceFinal: raw?.customerPriceFinal ?? raw?.CustomerPriceFinal ?? 0,
  taxAmount: raw?.taxAmount ?? raw?.TaxAmount,
  discountAmount: raw?.discountAmount ?? raw?.DiscountAmount,
  canApprove: raw?.canApprove ?? raw?.CanApprove,
  canRecalculate: raw?.canRecalculate ?? raw?.CanRecalculate,
  breakdown: raw?.breakdown ?? raw?.Breakdown,
  paymentSchedule: raw?.paymentSchedule ?? raw?.PaymentSchedule,
  suggestedLoanAmount: raw?.suggestedLoanAmount ?? raw?.SuggestedLoanAmount ?? 0,
  needsLoan: raw?.needsLoan ?? raw?.NeedsLoan ?? false,
  lineItems: raw?.lineItems ?? raw?.LineItems ?? [],
  approvedAt: raw?.approvedAt ?? raw?.ApprovedAt,
  customerAcceptedAt: raw?.customerAcceptedAt ?? raw?.CustomerAcceptedAt,
  contractAcceptance: raw?.contractAcceptance ?? raw?.ContractAcceptance,
});

const normalizeCostQuote = (raw) => ({
  id: raw?.id ?? raw?.Id,
  requestId: raw?.requestId ?? raw?.RequestId,
  quoteType: raw?.quoteType ?? raw?.QuoteType,
  quoteTypeTitle: raw?.quoteTypeTitle ?? raw?.QuoteTypeTitle ?? "",
  status: normalizeContractorCostQuoteStatus(raw?.status ?? raw?.Status),
  statusTitle: raw?.statusTitle ?? raw?.StatusTitle ?? "",
  revisionNo: raw?.revisionNo ?? raw?.RevisionNo ?? 1,
  executionCost: raw?.executionCost ?? raw?.ExecutionCost,
  equipmentCost: raw?.equipmentCost ?? raw?.EquipmentCost,
  logisticsCost: raw?.logisticsCost ?? raw?.LogisticsCost,
  otherCost: raw?.otherCost ?? raw?.OtherCost,
  totalContractorCost:
    raw?.totalContractorCost ?? raw?.TotalContractorCost ?? 0,
  contractorName: raw?.contractorName ?? raw?.ContractorName ?? "",
  packageTitle: raw?.packageTitle ?? raw?.PackageTitle ?? "",
  equipmentSnapshotId:
    raw?.equipmentSnapshotId ?? raw?.EquipmentSnapshotId ?? null,
  attachmentCount: raw?.attachmentCount ?? raw?.AttachmentCount ?? 0,
  submittedAt: raw?.submittedAt ?? raw?.SubmittedAt,
  acceptedAt: raw?.acceptedAt ?? raw?.AcceptedAt,
  acceptedByUserName: raw?.acceptedByUserName ?? raw?.AcceptedByUserName,
  rejectedAt: raw?.rejectedAt ?? raw?.RejectedAt,
  rejectReason: raw?.rejectReason ?? raw?.RejectReason,
  canReview: raw?.canReview ?? raw?.CanReview,
  lineItems: raw?.lineItems ?? raw?.LineItems ?? [],
  attachments: raw?.attachments ?? raw?.Attachments ?? [],
});

const costStatusChip = (status) => {
  const s = Number(status);
  if (s === COST_STATUS_APPROVED) return { color: "success", variant: "filled" };
  if (s === COST_STATUS_SUBMITTED) return { color: "warning", variant: "outlined" };
  if (s === COST_STATUS_REJECTED) return { color: "error", variant: "outlined" };
  return { color: "default", variant: "outlined" };
};

const formatMoney = (v) =>
  v > 0 ? Number(v).toLocaleString("fa-IR") : "—";

const WorkspacePricingSection = ({
  requestId,
  source = "SolarentOperator",
  onProgressChanged,
  openRecalcOnMount = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [costQuotes, setCostQuotes] = useState([]);
  const [legacyQuotes, setLegacyQuotes] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [actionLoadingId, setActionLoadingId] = useState(0);
  const [rejectOpenId, setRejectOpenId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [snapshotOpen, setSnapshotOpen] = useState(null);
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [attachmentsOpen, setAttachmentsOpen] = useState(null);
  const [customerOffers, setCustomerOffers] = useState([]);
  const [calcDialogOpen, setCalcDialogOpen] = useState(false);
  const [calcTargetCostQuoteId, setCalcTargetCostQuoteId] = useState(null);
  const [calcForm, setCalcForm] = useState({
    marginPercent: String(DEFAULT_CUSTOMER_OFFER_MARGIN_PERCENT),
    marginAmount: "",
    financingCost: "",
    platformServiceFee: "",
    warrantyExtensionCost: "",
    meterCost: "",
    gridConnectionInstallationCost: "",
    taxAmount: "",
    discountAmount: "",
    notes: "",
  });
  const [paymentForm, setPaymentForm] = useState(
    paymentScheduleToForm(buildDefaultPaymentSchedule(0, 0))
  );
  const [paymentManual, setPaymentManual] = useState(false);
  const [suggestedLoanAmount, setSuggestedLoanAmount] = useState(0);
  const [approvedLoanAmount, setApprovedLoanAmount] = useState(0);
  const [needsLoan, setNeedsLoan] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [contractAcceptance, setContractAcceptance] = useState(null);
  const [contractRejectOpen, setContractRejectOpen] = useState(false);
  const [contractRejectReason, setContractRejectReason] = useState("");
  const [projectContractSummary, setProjectContractSummary] = useState(null);
  const workflowProgressSyncedRef = useRef(false);
  const recalcOpenedRef = useRef(false);

  const approvedCostQuote = useMemo(
    () => costQuotes.find((q) => Number(q.status) === COST_STATUS_APPROVED),
    [costQuotes]
  );

  const approvedCustomerOffer = useMemo(
    () =>
      customerOffers.find(
        (o) =>
          Number(o.status) === OFFER_STATUS_APPROVED ||
          Number(o.status) === OFFER_STATUS_ACCEPTED
      ),
    [customerOffers]
  );

  const customerAcceptedOffer = useMemo(
    () => customerOffers.find((o) => Number(o.status) === OFFER_STATUS_ACCEPTED),
    [customerOffers]
  );

  const submittedCostQuotes = useMemo(
    () => costQuotes.filter((q) => Number(q.status) === COST_STATUS_SUBMITTED),
    [costQuotes]
  );

  const draftCostQuotes = useMemo(
    () => costQuotes.filter((q) => Number(q.status) === COST_STATUS_DRAFT),
    [costQuotes]
  );

  const pendingReviewCostQuotes = useMemo(
    () =>
      costQuotes.filter(
        (q) =>
          Number(q.status) === COST_STATUS_DRAFT ||
          Number(q.status) === COST_STATUS_SUBMITTED
      ),
    [costQuotes]
  );

  const previewCustomerFinal = useMemo(() => {
    if (!approvedCostQuote) {
      return 0;
    }
    return previewCustomerPriceFinal({
      contractorCost: approvedCostQuote.totalContractorCost,
      ...calcForm,
    });
  }, [approvedCostQuote, calcForm]);

  const previewCustomerVat = useMemo(() => {
    if (!approvedCostQuote) {
      return 0;
    }
    return previewCustomerOfferVat({
      contractorCost: approvedCostQuote.totalContractorCost,
      ...calcForm,
    });
  }, [approvedCostQuote, calcForm]);

  const syncPaymentDefaults = useCallback(
    (finalPrice, loanSuggestion) => {
      setPaymentForm(
        paymentScheduleToForm(buildDefaultPaymentSchedule(finalPrice, loanSuggestion))
      );
    },
    []
  );

  const loadSuggestedLoan = useCallback(
    async (finalPrice) => {
      if (!requestId) {
        return { suggested: 0, approved: 0, needs: false, disbursed: 0, schedule: null };
      }
      try {
        const res = await CustomerPriceOffersReviewApi.getPaymentScheduleDefaults(
          source,
          requestId,
          finalPrice || 0
        );
        if (!res.ok || !res.data) {
          return { suggested: 0, approved: 0, needs: false, disbursed: 0, schedule: null };
        }
        const approved =
          Number(res.data.approvedLoanAmount ?? res.data.ApprovedLoanAmount) || 0;
        const needs = Boolean(res.data.needsLoan ?? res.data.NeedsLoan);
        const disbursed =
          Number(
            res.data.loanDisbursementAmount ?? res.data.LoanDisbursementAmount
          ) || 0;
        const suggested =
          Number(res.data.suggestedLoanAmount ?? res.data.SuggestedLoanAmount) ||
          disbursed ||
          0;
        setApprovedLoanAmount(approved);
        setNeedsLoan(needs);
        setSuggestedLoanAmount(suggested || disbursed);
        return { suggested, approved, needs, disbursed, schedule: res.data };
      } catch {
        return { suggested: 0, approved: 0, needs: false, disbursed: 0, schedule: null };
      }
    },
    [requestId, source]
  );

  const resolveEffectiveLoanSuggestion = useCallback(
    (finalPrice, loanMeta) => {
      const final = Math.max(0, Math.round(Number(finalPrice) || 0));
      const disbursed =
        Number(loanMeta?.disbursed ?? loanMeta?.loanDisbursementAmount) || 0;
      if (disbursed > 0) {
        return Math.min(disbursed, final || disbursed);
      }
      const suggested =
        Number(loanMeta?.suggested ?? suggestedLoanAmount) || 0;
      if (suggested > 0) {
        return Math.min(suggested, final || suggested);
      }
      const approved = Number(loanMeta?.approved ?? approvedLoanAmount) || 0;
      if (approved > 0) {
        return Math.min(approved, final || approved);
      }
      return 0;
    },
    [approvedLoanAmount, suggestedLoanAmount]
  );

  const applyPaymentDefaultsFromMeta = useCallback(
    (finalPrice, loanMeta) => {
      if (loanMeta?.schedule) {
        setPaymentForm(
          paymentScheduleToForm(
            normalizePaymentSchedule(
              loanMeta.schedule,
              finalPrice,
              resolveEffectiveLoanSuggestion(finalPrice, loanMeta)
            )
          )
        );
        return;
      }
      syncPaymentDefaults(
        finalPrice,
        resolveEffectiveLoanSuggestion(finalPrice, loanMeta)
      );
    },
    [resolveEffectiveLoanSuggestion, syncPaymentDefaults]
  );

  const loadAll = useCallback(async () => {
    if (!requestId) return;
    setLoading(true);
    setError("");
    try {
      const settled = await Promise.allSettled([
        ContractorCostQuotesReviewApi.getCostQuotesByRequest(source, requestId),
        ProjectWorkflowFoundationApi.getQuotesByRequestId(source, requestId),
        CustomerPriceOffersReviewApi.getCustomerPriceOffersByRequest(source, requestId),
        CustomerContractAcceptanceReviewApi.getByRequest(source, requestId),
        ProjectWorkflowFoundationApi.getProjectContractWorkspaceSummary(source, requestId),
      ]);

      const unwrap = (result, label) => {
        if (result.status === "fulfilled") {
          return result.value;
        }
        const msg =
          result.reason?.response?.data?.message ||
          result.reason?.message ||
          label;
        return { ok: false, message: String(msg), data: null, _failed: true };
      };

      const costRes = unwrap(settled[0], "خطا در دریافت پیشنهادهای هزینه");
      const legacyRes = unwrap(settled[1], "خطا در دریافت پیشنهادهای قدیمی");
      const offerRes = unwrap(settled[2], "خطا در دریافت پیشنهاد قیمت مشتری");
      const contractRes = unwrap(settled[3], "خطا در دریافت وضعیت قرارداد");
      const projectContractRes = unwrap(settled[4], "خطا در دریافت خلاصه قرارداد");

      const failures = [
        !costRes.ok ? costRes.message || "خطا در دریافت پیشنهادهای هزینه" : null,
        !offerRes.ok && offerRes.message ? offerRes.message : null,
        !projectContractRes.ok && projectContractRes.message
          ? projectContractRes.message
          : null,
      ].filter(Boolean);

      if (!costRes.ok) {
        setCostQuotes([]);
      } else {
        const list = Array.isArray(costRes.data) ? costRes.data : [];
        setCostQuotes(list.map(normalizeCostQuote));
      }
      if (legacyRes.ok) {
        const legacy = Array.isArray(legacyRes.data) ? legacyRes.data : [];
        setLegacyQuotes(legacy.filter((x) => x?.isActive !== false));
      } else {
        setLegacyQuotes([]);
      }
      if (offerRes.ok) {
        const offers = Array.isArray(offerRes.data) ? offerRes.data : [];
        setCustomerOffers(offers.map(normalizeCustomerOffer));
      } else {
        setCustomerOffers([]);
      }
      if (contractRes.ok && contractRes.data) {
        const c = contractRes.data;
        setContractAcceptance({
          id: c?.id ?? c?.Id,
          reviewStatus: c?.reviewStatus ?? c?.ReviewStatus,
          reviewStatusTitle: c?.reviewStatusTitle ?? c?.ReviewStatusTitle ?? "",
          uploadedSignedContractUrl:
            c?.uploadedSignedContractUrl ?? c?.UploadedSignedContractUrl ?? "",
          canOperatorReview: c?.canOperatorReview ?? c?.CanOperatorReview,
        });
      } else {
        setContractAcceptance(null);
      }
      if (projectContractRes.ok && projectContractRes.data) {
        setProjectContractSummary(normalizeProjectContractSummary(projectContractRes.data));
      } else {
        setProjectContractSummary(null);
      }

      if (failures.length > 0) {
        setError(failures[0]);
      }
    } catch (e) {
      setError(e?.message || "خطا در بارگذاری بخش قیمت.");
    } finally {
      setLoading(false);
    }
  }, [requestId, source]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    workflowProgressSyncedRef.current = false;
  }, [requestId]);

  useEffect(() => {
    if (approvedCostQuote?.id) {
      setCalcTargetCostQuoteId(approvedCostQuote.id);
    }
  }, [approvedCostQuote?.id]);

  useEffect(() => {
    if (
      !openRecalcOnMount ||
      loading ||
      !approvedCostQuote?.id ||
      recalcOpenedRef.current
    ) {
      return;
    }
    recalcOpenedRef.current = true;
    openCalcDialog(approvedCostQuote.id);
  }, [openRecalcOnMount, loading, approvedCostQuote?.id]);

  useEffect(() => {
    if (!requestId || !approvedCostQuote || approvedCustomerOffer) {
      return;
    }
    let cancelled = false;
    (async () => {
      const initialFinal = previewCustomerPriceFinal({
        contractorCost: approvedCostQuote.totalContractorCost,
        marginPercent: DEFAULT_CUSTOMER_OFFER_MARGIN_PERCENT,
      });
      const meta = await loadSuggestedLoan(initialFinal);
      if (cancelled) return;
      setPaymentManual(false);
      applyPaymentDefaultsFromMeta(initialFinal, meta);
    })();
    return () => {
      cancelled = true;
    };
  }, [
    approvedCostQuote,
    approvedCustomerOffer,
    applyPaymentDefaultsFromMeta,
    loadSuggestedLoan,
    requestId,
  ]);

  useEffect(() => {
    if (paymentManual || !approvedCostQuote || approvedCustomerOffer) {
      return;
    }
    syncPaymentDefaults(
      previewCustomerFinal,
      resolveEffectiveLoanSuggestion(previewCustomerFinal)
    );
  }, [
    approvedCostQuote,
    approvedCustomerOffer,
    paymentManual,
    previewCustomerFinal,
    resolveEffectiveLoanSuggestion,
    syncPaymentDefaults,
  ]);

  useEffect(() => {
    if (
      loading ||
      !onProgressChanged ||
      workflowProgressSyncedRef.current ||
      !approvedCustomerOffer
    ) {
      return;
    }

    workflowProgressSyncedRef.current = true;
    onProgressChanged({ navigateToStage: true });
  }, [approvedCustomerOffer, loading, onProgressChanged]);

  const handleAccept = async (costQuoteId) => {
    setActionLoadingId(costQuoteId);
    setError("");
    setSuccess("");
    try {
      const res = await ContractorCostQuotesReviewApi.acceptContractorCostQuote(
        source,
        { costQuoteId, note: "" }
      );
      if (!res.ok) {
        setError(res.message || "تایید هزینه پیمانکار ناموفق بود.");
        return;
      }
      setSuccess(res.message || "هزینه پیمانکار تایید شد.");
      if (res.data) {
        const accepted = normalizeCostQuote(res.data);
        setCostQuotes((prev) =>
          prev.map((q) => (q.id === accepted.id ? { ...q, ...accepted } : q))
        );
        setCalcTargetCostQuoteId(accepted.id);
      }
      await loadAll();
      if (onProgressChanged) {
        await onProgressChanged({ navigateToStage: false });
      }
    } catch {
      setError("خطا در تایید هزینه پیمانکار.");
    } finally {
      setActionLoadingId(0);
    }
  };

  const handleReject = async () => {
    if (!rejectOpenId || !rejectReason.trim()) {
      setError("دلیل رد الزامی است.");
      return;
    }
    setActionLoadingId(rejectOpenId);
    setError("");
    try {
      const res = await ContractorCostQuotesReviewApi.rejectContractorCostQuote(
        source,
        { costQuoteId: rejectOpenId, reason: rejectReason.trim() }
      );
      if (!res.ok) {
        setError(res.message || "رد پیشنهاد هزینه ناموفق بود.");
        return;
      }
      setSuccess(res.message || "پیشنهاد هزینه رد شد.");
      setRejectOpenId(null);
      setRejectReason("");
      await loadAll();
    } catch {
      setError("خطا در رد پیشنهاد هزینه.");
    } finally {
      setActionLoadingId(0);
    }
  };

  const openCalcDialog = async (costQuoteId) => {
    setCalcTargetCostQuoteId(costQuoteId);
    setCalcForm((prev) => ({
      ...prev,
      marginPercent:
        prev.marginPercent?.trim()?.length > 0
          ? prev.marginPercent
          : String(DEFAULT_CUSTOMER_OFFER_MARGIN_PERCENT),
    }));
    setPaymentManual(false);
    setCalcDialogOpen(true);
    const meta = await loadSuggestedLoan(previewCustomerFinal);
    applyPaymentDefaultsFromMeta(previewCustomerFinal, meta);
  };

  const buildCalcPayload = () => {
    const useAmount = calcForm.marginAmount?.trim()?.length > 0;
    const costQuoteId = calcTargetCostQuoteId ?? approvedCostQuote?.id;
    const marginPercentRaw = Number(calcForm.marginPercent);
    const schedule = paymentScheduleFromForm(paymentForm, previewCustomerFinal);
    return {
      contractorCostQuoteId: costQuoteId,
      marginAmount: useAmount ? Number(calcForm.marginAmount) : null,
      marginPercent: useAmount
        ? null
        : marginPercentRaw > 0
          ? marginPercentRaw
          : DEFAULT_CUSTOMER_OFFER_MARGIN_PERCENT,
      financingCost: Number(calcForm.financingCost) || null,
      platformServiceFee: Number(calcForm.platformServiceFee) || null,
      warrantyExtensionCost: Number(calcForm.warrantyExtensionCost) || null,
      meterCost: Number(calcForm.meterCost) || null,
      gridConnectionInstallationCost:
        Number(calcForm.gridConnectionInstallationCost) || null,
      discountAmount: Number(calcForm.discountAmount) || null,
      notes: calcForm.notes || "",
      paymentSchedule: {
        loanDisbursementAmount: schedule.loanDisbursementAmount,
        remainingFirstHalfAmount: schedule.remainingFirstHalfAmount,
        remainingSecondHalfAmount: schedule.remainingSecondHalfAmount,
        loanStageTitle: PAYMENT_SCHEDULE_TITLES.loan,
        remainingFirstHalfTitle: PAYMENT_SCHEDULE_TITLES.remainingFirst,
        remainingSecondHalfTitle: PAYMENT_SCHEDULE_TITLES.remainingSecond,
      },
    };
  };

  const handlePaymentLoanChange = (value) => {
    setPaymentManual(true);
    const final = Math.max(0, Math.round(Number(previewCustomerFinal) || 0));
    const loan = Math.min(Math.max(0, Math.round(Number(value) || 0)), final);
    const schedule = buildDefaultPaymentSchedule(final, loan);
    setPaymentForm({
      loan: String(schedule.loanDisbursementAmount),
      remainingFirst: String(schedule.remainingFirstHalfAmount),
      remainingSecond: String(schedule.remainingSecondHalfAmount),
    });
  };

  const handlePaymentHalfChange = (field, value) => {
    setPaymentManual(true);
    const final = Math.max(0, Math.round(Number(previewCustomerFinal) || 0));
    const loan = Math.min(Math.max(0, Math.round(Number(paymentForm.loan) || 0)), final);
    const remaining = final - loan;
    let amount = Math.max(0, Math.round(Number(value) || 0));
    if (amount > remaining) amount = remaining;
    if (field === "remainingFirst") {
      setPaymentForm({
        loan: String(loan),
        remainingFirst: String(amount),
        remainingSecond: String(remaining - amount),
      });
      return;
    }
    setPaymentForm({
      loan: String(loan),
      remainingFirst: String(remaining - amount),
      remainingSecond: String(amount),
    });
  };

  const handleCreateCustomerOffer = async () => {
    const targetId = calcTargetCostQuoteId ?? approvedCostQuote?.id;
    if (!targetId) return;
    if (
      !hasCustomerOfferPlatformMargin({
        marginPercent: calcForm.marginPercent,
        marginAmount: calcForm.marginAmount,
      })
    ) {
      setError(CUSTOMER_OFFER_MISSING_MARGIN_MESSAGE);
      return;
    }
    setActionLoadingId(targetId);
    setError("");
    try {
      const res = await CustomerPriceOffersReviewApi.createCustomerPriceOffer(source, {
        ...buildCalcPayload(),
        contractorCostQuoteId: targetId,
      });
      if (!res.ok) {
        setError(res.message || "محاسبه قیمت مشتری ناموفق بود.");
        return;
      }
      setSuccess(res.message || "پیشنهاد قیمت مشتری محاسبه شد.");
      setCalcDialogOpen(false);
      setSelectedOffer(normalizeCustomerOffer(res.data));
      await loadAll();
      if (onProgressChanged) {
        await onProgressChanged({ navigateToStage: true });
      }
    } catch {
      setError("خطا در محاسبه قیمت مشتری.");
    } finally {
      setActionLoadingId(0);
    }
  };

  const handleApproveCustomerOffer = async (offerId) => {
    const offer = customerOffers.find((o) => Number(o.id) === Number(offerId));
    if (
      offer &&
      !hasCustomerOfferPlatformMargin({
        breakdown: offer.breakdown,
        customerPriceFinal: offer.customerPriceFinal,
        totalContractorCost: offer.totalContractorCost,
      })
    ) {
      setError(CUSTOMER_OFFER_MISSING_MARGIN_MESSAGE);
      return;
    }
    setActionLoadingId(offerId);
    setError("");
    try {
      const res = await CustomerPriceOffersReviewApi.approveCustomerPriceOffer(source, {
        customerPriceOfferId: offerId,
        note: "",
      });
      if (!res.ok) {
        setError(res.message || "تایید قیمت مشتری ناموفق بود.");
        return;
      }
      setSuccess(
        res.message ||
          "قیمت تایید شد؛ قرارداد متقاضی آماده است. در پنل کارمزد و تخفیف بمانید یا در صورت نیاز محاسبه مجدد انجام دهید."
      );
      await loadAll();
      if (onProgressChanged) {
        await onProgressChanged({ navigateToStage: true });
      }
    } catch {
      setError("خطا در تایید قیمت و آماده‌سازی قرارداد.");
    } finally {
      setActionLoadingId(0);
    }
  };

  const renderCustomerPriceCalcFields = () => (
    <Stack spacing={2}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            size="small"
            label="درصد حاشیه / کارمزد سولارنت"
            type="number"
            value={calcForm.marginPercent}
            onChange={(e) =>
              setCalcForm((p) => ({ ...p, marginPercent: e.target.value, marginAmount: "" }))
            }
            helperText="مثلاً ۱۲ برای اعمال ۱۲٪ روی هزینه پیمانکار"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            size="small"
            label="مبلغ حاشیه (اختیاری)"
            type="number"
            value={calcForm.marginAmount}
            onChange={(e) =>
              setCalcForm((p) => ({ ...p, marginAmount: e.target.value }))
            }
            helperText="در صورت پر شدن، جایگزین درصد می‌شود"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            size="small"
            label="هزینه تامین مالی"
            type="number"
            value={calcForm.financingCost}
            onChange={(e) =>
              setCalcForm((p) => ({ ...p, financingCost: e.target.value }))
            }
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            size="small"
            label="کارمزد خدمات پلتفرم (مبلغ ریال)"
            type="number"
            value={calcForm.platformServiceFee}
            onChange={(e) =>
              setCalcForm((p) => ({ ...p, platformServiceFee: e.target.value }))
            }
            helperText="مبلغ ثابت ریالی؛ برای درصد از فیلد حاشیه استفاده کنید"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            size="small"
            label="هزینه کنتور (ریال)"
            type="number"
            value={calcForm.meterCost}
            onChange={(e) =>
              setCalcForm((p) => ({ ...p, meterCost: e.target.value }))
            }
            helperText="معمولاً خالی بگذارید؛ خرید کنتور بر عهده متقاضی است و در قرارداد سولارنت نیست"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            size="small"
            label="بسته اتصال به شبکه (ریال)"
            type="number"
            value={calcForm.gridConnectionInstallationCost}
            onChange={(e) =>
              setCalcForm((p) => ({
                ...p,
                gridConnectionInstallationCost: e.target.value,
              }))
            }
            helperText="پیشنهاد: ۲۰٬۰۰۰٬۰۰۰ ریال (۱۰م توزیع + ۱۰م کارمزد پیگیری). اگر اینجا وارد شود در قیمت نهایی می‌آید؛ در غیر این صورت با تیک قرارداد اضافه می‌شود"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            size="small"
            label="گارانتی / تمدید"
            type="number"
            value={calcForm.warrantyExtensionCost}
            onChange={(e) =>
              setCalcForm((p) => ({ ...p, warrantyExtensionCost: e.target.value }))
            }
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            size="small"
            label="تخفیف"
            type="number"
            value={calcForm.discountAmount}
            onChange={(e) =>
              setCalcForm((p) => ({ ...p, discountAmount: e.target.value }))
            }
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            size="small"
            label="مالیات بر ارزش افزوده ۱۰٪ (محاسبه‌شده)"
            value={formatMoney(previewCustomerVat)}
            InputProps={{ readOnly: true }}
            helperText="خودکار: ۱۰٪ روی (پیمانکار + کارمزد پلتفرم و سایر اقلام − تخفیف)؛ کنتور و بسته اتصال به شبکه مشمول مالیات نیستند"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            size="small"
            label="قیمت نهایی پیشنهادی (محاسبه‌شده)"
            value={formatMoney(previewCustomerFinal)}
            InputProps={{ readOnly: true }}
            helperText="پیمانکار + کارمزد + مالیات ۱۰٪. در قرارداد، بسته کنتور/انشعاب بدون مالیات به این مبلغ اضافه می‌شود"
          />
        </Grid>
        <Grid item xs={12}>
          <Divider sx={{ my: 0.5 }} />
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
            نحوه پرداخت
          </Typography>
          <Alert severity="info" sx={{ mb: 1.5 }}>
            پیش‌فرض: وام واریزی از جدول واریزی‌ها (شماره پیگیری وام)
            {suggestedLoanAmount > 0 || approvedLoanAmount > 0
              ? ` — ${formatMoney(
                  resolveEffectiveLoanSuggestion(previewCustomerFinal)
                )} ریال`
              : needsLoan
                ? " — هنوز واریزی ثبت نشده"
                : ""}
            ، سپس ۹۰ درصد آورده متقاضی پیش از اجرا و ۱۰ درصد پس از تحویل پروژه. در صورت نیاز مقادیر را ویرایش کنید.
          </Alert>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            size="small"
            label={PAYMENT_SCHEDULE_TITLES.loan}
            type="number"
            value={paymentForm.loan}
            onChange={(e) => handlePaymentLoanChange(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            size="small"
            label={PAYMENT_SCHEDULE_TITLES.remainingFirst}
            type="number"
            value={paymentForm.remainingFirst}
            onChange={(e) => handlePaymentHalfChange("remainingFirst", e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            size="small"
            label={PAYMENT_SCHEDULE_TITLES.remainingSecond}
            type="number"
            value={paymentForm.remainingSecond}
            onChange={(e) => handlePaymentHalfChange("remainingSecond", e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            label="یادداشت"
            multiline
            minRows={2}
            value={calcForm.notes}
            onChange={(e) => setCalcForm((p) => ({ ...p, notes: e.target.value }))}
          />
        </Grid>
      </Grid>
    </Stack>
  );

  const openSnapshot = async (item) => {
    const snapshotId = item?.equipmentSnapshotId;
    if (!snapshotId) return;
    setSnapshotLoading(true);
    setSnapshotOpen({ title: "ریز تجهیزات (snapshot)", items: [] });
    try {
      const res = await ContractorCostQuotesReviewApi.getEquipmentSnapshot(
        source,
        snapshotId
      );
      if (res.ok) {
        setSnapshotOpen({
          title: `ریز تجهیزات — نسخه ${res.data?.snapshotVersion ?? ""}`,
          items: res.data?.items ?? res.data?.Items ?? [],
          packageTitle:
            res.data?.sourcePackageTitle ?? res.data?.SourcePackageTitle,
        });
      } else {
        setError(res.message || "خطا در دریافت snapshot");
        setSnapshotOpen(null);
      }
    } catch {
      setError("خطا در دریافت ریز تجهیزات.");
      setSnapshotOpen(null);
    } finally {
      setSnapshotLoading(false);
    }
  };

  const canReview = (item) =>
    item?.canReview === true ||
    Number(item?.status) === COST_STATUS_SUBMITTED ||
    Number(item?.status) === COST_STATUS_DRAFT;

  return (
    <Stack spacing={2} data-testid="contractor-cost-quote">
      <Typography variant="h6" fontWeight={700}>
        قیمت و هزینه پیمانکار
      </Typography>
      <Alert severity="info">
        مسیر جدید: پیشنهاد هزینه پیمانکار (ContractorCostQuote). تایید هزینه فقط
        برای محاسبه قیمت مشتری است — در این فاز قرارداد/فاکتور ایجاد نمی‌شود.
      </Alert>

      {error ? (
        <Alert severity="error" onClose={() => setError("")}>
          {error}
        </Alert>
      ) : null}
      {success ? (
        <Alert severity="success" onClose={() => setSuccess("")}>
          {success}
        </Alert>
      ) : null}

      <Typography variant="subtitle2" fontWeight={700}>
        پیشنهادهای هزینه پیمانکار (جدید)
      </Typography>
      {loading ? (
        <Typography variant="body2" color="text.secondary">
          در حال بارگذاری...
        </Typography>
      ) : costQuotes.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          هنوز پیشنهاد هزینه پیمانکار ثبت نشده است.
        </Typography>
      ) : (
        <Grid container spacing={1}>
          {costQuotes.map((item) => (
            <Grid item xs={12} key={item.id}>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderWidth: Number(item.status) === COST_STATUS_APPROVED ? 2 : 1,
                  borderColor:
                    Number(item.status) === COST_STATUS_APPROVED
                      ? "success.main"
                      : "divider",
                }}
              >
                <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
                  <Chip size="small" label="هزینه پیمانکار" color="primary" />
                  <Chip size="small" label={`نوع: ${item.quoteTypeTitle || "—"}`} />
                  <Chip
                    size="small"
                    label={`وضعیت: ${item.statusTitle || "—"}`}
                    {...costStatusChip(item.status)}
                  />
                  <Chip size="small" variant="outlined" label={`نسخه: ${item.revisionNo}`} />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`جمع کل: ${formatMoney(item.totalContractorCost)}`}
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`اجرا: ${formatMoney(item.executionCost)}`}
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`تجهیزات: ${formatMoney(item.equipmentCost)}`}
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`پیمانکار: ${item.contractorName || "—"}`}
                  />
                  {item.packageTitle ? (
                    <Chip size="small" variant="outlined" label={`پکیج: ${item.packageTitle}`} />
                  ) : null}
                  <Stack direction="row" spacing={1} sx={{ ml: "auto" }}>
                    {item.equipmentSnapshotId ? (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<VisibilityIcon />}
                        onClick={() => openSnapshot(item)}
                      >
                        ریز تجهیزات
                      </Button>
                    ) : null}
                    {item.attachmentCount > 0 ? (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={async () => {
                          const res =
                            await ContractorCostQuotesReviewApi.getCostQuoteDetails(
                              source,
                              item.id
                            );
                          if (res.ok) {
                            setAttachmentsOpen(normalizeCostQuote(res.data));
                          }
                        }}
                      >
                        پیوست ({item.attachmentCount})
                      </Button>
                    ) : null}
                    {canReview(item) ? (
                      <>
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          disabled={actionLoadingId === item.id}
                          onClick={() => handleAccept(item.id)}
                        >
                          تایید هزینه پیمانکار
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          disabled={actionLoadingId === item.id}
                          onClick={() => {
                            setRejectOpenId(item.id);
                            setRejectReason("");
                          }}
                        >
                          رد
                        </Button>
                      </>
                    ) : null}
                  </Stack>
                </Stack>
                {Number(item.status) === COST_STATUS_DRAFT ? (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    پیش‌نویس — کارشناس می‌تواند پس از بررسی، هزینه را تایید کند (ارسال توسط پیمانکار
                    الزامی نیست).
                  </Typography>
                ) : null}
                {item.rejectedAt ? (
                  <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                    رد: {toJalaliFullDate(item.rejectedAt)} — {item.rejectReason || ""}
                  </Typography>
                ) : null}
                {item.acceptedAt ? (
                  <Typography variant="caption" color="success.main" display="block" sx={{ mt: 0.5 }}>
                    تایید: {toJalaliFullDate(item.acceptedAt)}
                    {item.acceptedByUserName ? ` (${item.acceptedByUserName})` : ""}
                  </Typography>
                ) : null}
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      <Divider />

      <Typography variant="subtitle2" fontWeight={700}>
        قیمت پیشنهادی مشتری (سولارنت)
      </Typography>
      {!approvedCostQuote && pendingReviewCostQuotes.length > 0 ? (
        <Alert severity="warning">
          {draftCostQuotes.length > 0 && submittedCostQuotes.length === 0
            ? "پیشنهاد(های) پیمانکار هنوز ارسال نشده‌اند (پیش‌نویس). یکی را بررسی و «تایید هزینه پیمانکار» بزنید تا دکمه «محاسبه قیمت مشتری» فعال شود."
            : "یک پیشنهاد هزینه پیمانکار را «تایید هزینه پیمانکار» کنید، سپس «محاسبه قیمت مشتری» را بزنید."}
        </Alert>
      ) : null}
      {approvedCostQuote && !approvedCustomerOffer && customerOffers.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 2, bgcolor: "action.hover" }}>
          <Stack spacing={2}>
            <Alert severity="info" sx={{ mb: 0 }}>
              هزینه پیمانکار تایید شد (
              {formatMoney(approvedCostQuote.totalContractorCost)} ریال). پارامترهای
              زیر را تنظیم کنید و پیش‌نویس قیمت مشتری را ثبت کنید. درصد حاشیه سولارنت
              (پیش‌فرض {DEFAULT_CUSTOMER_OFFER_MARGIN_PERCENT}٪) الزامی است.
            </Alert>
            {renderCustomerPriceCalcFields()}
            {!hasCustomerOfferPlatformMargin({
              marginPercent: calcForm.marginPercent,
              marginAmount: calcForm.marginAmount,
            }) ? (
              <Alert severity="error">{CUSTOMER_OFFER_MISSING_MARGIN_MESSAGE}</Alert>
            ) : null}
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                color="primary"
                disabled={
                  actionLoadingId > 0 ||
                  previewCustomerFinal <= 0 ||
                  !hasCustomerOfferPlatformMargin({
                    marginPercent: calcForm.marginPercent,
                    marginAmount: calcForm.marginAmount,
                  })
                }
                onClick={handleCreateCustomerOffer}
              >
                ثبت پیش‌نویس قیمت مشتری
              </Button>
            </Stack>
          </Stack>
        </Paper>
      ) : null}
      {approvedCostQuote ? (
        <Button
          variant="outlined"
          color="primary"
          disabled={actionLoadingId > 0}
          onClick={() => openCalcDialog(approvedCostQuote.id)}
        >
          محاسبه مجدد قیمت مشتری
        </Button>
      ) : null}
      {approvedCustomerOffer ? (
        <Alert severity="success">
          {customerAcceptedOffer
            ? "مشتری قیمت را پذیرفته است."
            : "قیمت مشتری توسط سولارنت تایید شد — در انتظار پاسخ مشتری."}
        </Alert>
      ) : null}

      {customerOffers.length === 0 && !approvedCostQuote ? (
        <Typography variant="body2" color="text.secondary">
          هنوز پیشنهاد قیمت مشتری ثبت نشده است.
        </Typography>
      ) : customerOffers.length === 0 ? null : (
        <Grid container spacing={1}>
          {customerOffers.map((offer) => (
            <Grid item xs={12} key={offer.id}>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderWidth: Number(offer.status) === OFFER_STATUS_APPROVED ? 2 : 1,
                  borderColor:
                    Number(offer.status) === OFFER_STATUS_APPROVED
                      ? "success.main"
                      : "divider",
                }}
              >
                <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
                  <Chip size="small" color="secondary" label="قیمت مشتری" />
                  <Chip size="small" label={`وضعیت: ${offer.statusTitle}`} />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`هزینه پیمانکار: ${formatMoney(offer.totalContractorCost)}`}
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`جمع جزئی: ${formatMoney(offer.customerPriceSubtotal)}`}
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`قیمت نهایی: ${formatMoney(offer.customerPriceFinal)}`}
                  />
                  {offer.paymentSchedule ? (
                    <>
                      <Chip
                        size="small"
                        color="info"
                        variant="outlined"
                        label={`وام واریزی: ${formatMoney(
                          offer.paymentSchedule.loanDisbursementAmount ??
                            offer.paymentSchedule.LoanDisbursementAmount
                        )}`}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`باقیمانده ۱: ${formatMoney(
                          offer.paymentSchedule.remainingFirstHalfAmount ??
                            offer.paymentSchedule.RemainingFirstHalfAmount
                        )}`}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`نصب / باقیمانده ۲: ${formatMoney(
                          offer.paymentSchedule.remainingSecondHalfAmount ??
                            offer.paymentSchedule.RemainingSecondHalfAmount
                        )}`}
                      />
                    </>
                  ) : null}
                  <Stack direction="row" spacing={1} sx={{ ml: "auto" }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={async () => {
                        const res =
                          await CustomerPriceOffersReviewApi.getCustomerPriceOfferDetails(
                            source,
                            offer.id
                          );
                        if (res.ok) setSelectedOffer(normalizeCustomerOffer(res.data));
                      }}
                    >
                      breakdown
                    </Button>
                    {Number(offer.status) === OFFER_STATUS_DRAFT ? (
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        disabled={
                          actionLoadingId === offer.id ||
                          offer.canApprove === false ||
                          !hasCustomerOfferPlatformMargin({
                            breakdown: offer.breakdown,
                            customerPriceFinal: offer.customerPriceFinal,
                            totalContractorCost: offer.totalContractorCost,
                          })
                        }
                        onClick={() => handleApproveCustomerOffer(offer.id)}
                        title={
                          hasCustomerOfferPlatformMargin({
                            breakdown: offer.breakdown,
                            customerPriceFinal: offer.customerPriceFinal,
                            totalContractorCost: offer.totalContractorCost,
                          })
                            ? undefined
                            : CUSTOMER_OFFER_MISSING_MARGIN_MESSAGE
                        }
                      >
                        تایید قیمت مشتری و آماده‌سازی قرارداد
                      </Button>
                    ) : null}
                  </Stack>
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {contractAcceptance ? (
        <Paper variant="outlined" sx={{ p: 1.5 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
            قرارداد مشتری-سولارنت
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
            <Chip size="small" label={`وضعیت: ${contractAcceptance.reviewStatusTitle}`} />
            {contractAcceptance.uploadedSignedContractUrl ? (
              <Chip
                size="small"
                variant="outlined"
                label="فایل آپلود شده"
                color="info"
              />
            ) : null}
            {contractAcceptance.canOperatorReview ? (
              <>
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  disabled={actionLoadingId > 0}
                  onClick={async () => {
                    setActionLoadingId(contractAcceptance.id);
                    const res =
                      await CustomerContractAcceptanceReviewApi.reviewSignedCustomerContract(
                        source,
                        {
                          customerContractAcceptanceId: contractAcceptance.id,
                          approve: true,
                        }
                      );
                    if (res.ok) {
                      setSuccess(res.message || "قرارداد تایید شد.");
                      loadAll();
                    } else {
                      setError(res.message);
                    }
                    setActionLoadingId(0);
                  }}
                >
                  تایید قرارداد امضا‌شده
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={() => {
                    setContractRejectOpen(true);
                    setContractRejectReason("");
                  }}
                >
                  رد قرارداد
                </Button>
              </>
            ) : null}
          </Stack>
        </Paper>
      ) : null}

      {Number(contractAcceptance?.reviewStatus) === CONTRACT_REVIEW_APPROVED ||
      projectContractSummary?.projectContractId ? (
        <Paper variant="outlined" sx={{ p: 1.5 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
            قرارداد اجرای پروژه
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
            {projectContractSummary?.projectContractId ? (
              <>
                <Chip
                  size="small"
                  color="success"
                  label={`قرارداد #${projectContractSummary.projectContractId}`}
                />
                <Chip
                  size="small"
                  variant="outlined"
                  label={`مبلغ قرارداد: ${formatMoney(
                    projectContractSummary.contractAmount ?? projectContractSummary.price
                  )}`}
                />
                {projectContractSummary.projectContractStatusTitle ? (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={projectContractSummary.projectContractStatusTitle}
                  />
                ) : null}
              </>
            ) : null}
            {projectContractSummary?.canGenerateFromCustomerOffer ? (
              <Button
                size="small"
                variant="contained"
                disabled={actionLoadingId > 0}
                onClick={async () => {
                  setActionLoadingId(requestId);
                  const res =
                    await ProjectWorkflowFoundationApi.generateProjectContractFromCustomerOffer(
                      source,
                      { requestId }
                    );
                  if (res.ok) {
                    setSuccess(res.message || "قرارداد اجرا ایجاد شد.");
                    loadAll();
                  } else {
                    setError(res.message);
                  }
                  setActionLoadingId(0);
                }}
              >
                ایجاد قرارداد اجرا
              </Button>
            ) : null}
            {!projectContractSummary?.canGenerateFromCustomerOffer &&
            projectContractSummary?.generateBlockReason &&
            !projectContractSummary?.projectContractId ? (
              <Typography variant="caption" color="text.secondary">
                {projectContractSummary.generateBlockReason}
              </Typography>
            ) : null}
          </Stack>
        </Paper>
      ) : null}

      {selectedOffer?.lineItems?.length > 0 ? (
        <Paper variant="outlined" sx={{ p: 1.5 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            ریز اقلام قیمت مشتری
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>نوع</TableCell>
                <TableCell>عنوان</TableCell>
                <TableCell>مبلغ</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedOffer.lineItems.map((row, idx) => (
                <TableRow key={row.id ?? idx}>
                  <TableCell>{row.lineTypeTitle ?? row.LineTypeTitle}</TableCell>
                  <TableCell>{row.title ?? row.Title}</TableCell>
                  <TableCell>
                    {formatMoney(row.totalPrice ?? row.TotalPrice)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      ) : null}

      {legacyQuotes.length > 0 ? (
        <>
          <Divider />
          <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
            پیشنهادهای قیمت قدیمی (Legacy — فقط مشاهده)
          </Typography>
          <Grid container spacing={1}>
            {legacyQuotes.map((item) => (
              <Grid item xs={12} key={`legacy-${item.id}`}>
                <Paper variant="outlined" sx={{ p: 1, opacity: 0.85 }}>
                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    <Chip size="small" label="Legacy" color="default" variant="filled" />
                    <Chip size="small" label={item.projectQuoteTypeTitle || "—"} />
                    <Chip size="small" label={item.projectQuoteStatusTitle || "—"} />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`مبلغ: ${formatMoney(item.finalPrice || item.basePrice || item.amount)}`}
                    />
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </>
      ) : null}

      <Dialog open={Boolean(rejectOpenId)} onClose={() => setRejectOpenId(null)} fullWidth maxWidth="sm">
        <DialogTitle>رد پیشنهاد هزینه پیمانکار</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="دلیل رد"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpenId(null)}>انصراف</Button>
          <Button color="error" variant="contained" onClick={handleReject}>
            ثبت رد
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(snapshotOpen)}
        onClose={() => setSnapshotOpen(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{snapshotOpen?.title || "ریز تجهیزات"}</DialogTitle>
        <DialogContent>
          {snapshotOpen?.packageTitle ? (
            <Typography variant="body2" sx={{ mb: 1 }}>
              پکیج منبع: {snapshotOpen.packageTitle}
            </Typography>
          ) : null}
          {snapshotLoading ? (
            <Typography>در حال بارگذاری...</Typography>
          ) : (
            <Table size="small">
                <TableHead>
                <TableRow>
                  <TableCell>کد</TableCell>
                  <TableCell>نوع</TableCell>
                  <TableCell>برند</TableCell>
                  <TableCell>مدل</TableCell>
                  <TableCell>تعداد</TableCell>
                  <TableCell>واحد</TableCell>
                  <TableCell>قیمت واحد</TableCell>
                  <TableCell>جمع</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(snapshotOpen?.items || []).map((row, idx) => (
                  <TableRow key={row.id ?? idx}>
                    <TableCell>
                      <EquipmentPublicCodeChip
                        code={row.publicCode ?? row.PublicCode}
                      />
                    </TableCell>
                    <TableCell>{row.equipmentType ?? row.EquipmentType}</TableCell>
                    <TableCell>{row.brand ?? row.Brand}</TableCell>
                    <TableCell>{row.model ?? row.Model}</TableCell>
                    <TableCell>{row.quantity ?? row.Quantity}</TableCell>
                    <TableCell>{row.unit ?? row.Unit ?? "—"}</TableCell>
                    <TableCell>
                      {formatMoney(row.unitCost ?? row.UnitCost)}
                    </TableCell>
                    <TableCell>
                      {formatMoney(row.totalCost ?? row.TotalCost)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSnapshotOpen(null)}>بستن</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={calcDialogOpen} onClose={() => setCalcDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>محاسبه مجدد قیمت مشتری</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>{renderCustomerPriceCalcFields()}</Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCalcDialogOpen(false)}>انصراف</Button>
          <Button
            variant="contained"
            disabled={
              actionLoadingId > 0 ||
              !hasCustomerOfferPlatformMargin({
                marginPercent: calcForm.marginPercent,
                marginAmount: calcForm.marginAmount,
              })
            }
            onClick={handleCreateCustomerOffer}
          >
            محاسبه و ثبت پیش‌نویس
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(attachmentsOpen)}
        onClose={() => setAttachmentsOpen(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>پیوست‌های پیشنهاد هزینه</DialogTitle>
        <DialogContent>
          {(attachmentsOpen?.attachments || []).length === 0 ? (
            <Typography variant="body2">پیوستی ثبت نشده است.</Typography>
          ) : (
            (attachmentsOpen.attachments || []).map((att) => (
              <Box key={att.id ?? att.Id} sx={{ py: 0.5 }}>
                <Typography variant="body2">
                  {att.originalFileName ?? att.OriginalFileName ?? "فایل"}
                  {att.fileUrl || att.FileUrl ? (
                    <>
                      {" "}
                      —{" "}
                      <a href={att.fileUrl || att.FileUrl} target="_blank" rel="noreferrer">
                        دانلود
                      </a>
                    </>
                  ) : null}
                </Typography>
              </Box>
            ))
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAttachmentsOpen(null)}>بستن</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={contractRejectOpen}
        onClose={() => setContractRejectOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>رد قرارداد امضا‌شده</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="دلیل رد"
            value={contractRejectReason}
            onChange={(e) => setContractRejectReason(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContractRejectOpen(false)}>انصراف</Button>
          <Button
            color="error"
            variant="contained"
            disabled={!contractRejectReason.trim() || actionLoadingId > 0}
            onClick={async () => {
              if (!contractAcceptance?.id) return;
              setActionLoadingId(contractAcceptance.id);
              const res =
                await CustomerContractAcceptanceReviewApi.reviewSignedCustomerContract(
                  source,
                  {
                    customerContractAcceptanceId: contractAcceptance.id,
                    approve: false,
                    reason: contractRejectReason.trim(),
                  }
                );
              if (res.ok) {
                setSuccess(res.message || "قرارداد رد شد.");
                setContractRejectOpen(false);
                loadAll();
              } else {
                setError(res.message);
              }
              setActionLoadingId(0);
            }}
          >
            ثبت رد
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default WorkspacePricingSection;
